<template>
  <div style="height: 100%; width: 100%;">
    <div id="leaflet-map-container">
      <div class="time-legend-container" v-if="historyPoints.length > 0">
        <div class="legend-title">Tiempo</div>
        <span class="label top">Más Reciente</span>
        <div class="color-scale">
            <div class="color-bar"></div>
        </div>
        <span class="label bottom">Más Antiguo</span>
    </div>
    </div>
    <vs-popup
      title="Imagen Detalle"
      :active.sync="popupActivo"
      class="dynamic-image-popup"
      :style="{ minWidth: '400px' }"
    >
      <div class="image-popup-wrapper">
        <template v-if="currentThumbnailData">
          <Thumbnails
            :img="getThumbnailSrc(currentThumbnailData)"
          />
        </template>
        <template v-else>
          <div class="text-center p-8 text-gray-500">
            Cargando imagen o datos no disponibles.
          </div>
        </template>
      </div>
    </vs-popup>
  </div>
</template>

<script>
// === Importar Leaflet y PolylineDecorator correctamente ===
import L from 'leaflet'; 
import 'leaflet/dist/leaflet.css';
import 'leaflet-polylinedecorator'; 
import moment from 'moment';
import Thumbnails from "./Thumbnails";

// Importa los iconos de Leaflet (requerido para que L.icon funcione en algunos setups de Vue)
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Corrige la ruta de los iconos
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

export default {
  name: 'LeafletMap',
  props: {
    // Array de objetos con {lat, lon, lastka_time, gsm_signal, thumbnail, velocity, ...}
    historyPoints: {
      type: Array,
      default: () => [],
    },
    // PROP 1: Controla si se muestran números en los marcadores de historial
    showNumberedMarkers: {
      type: Boolean,
      default: false,
    },
    // PROP 2: Controla si se dibuja la línea de ruta (polilínea)
    showFixedRoute: {
      type: Boolean,
      default: false,
    }
    // NOTA: El color por tiempo ahora siempre se aplica a los puntos de historial,
    // y showFixedRoute controla la línea azul.
  },
  components: {
    Thumbnails
  },
  data() {
    return {
      map: null,
      historyLayer: null, // LayerGroup para gestionar polilínea y marcadores
      defaultCoordinates: [-22.9799, -69.0700], 
      
      popupActivo: false,
      currentThumbnailData: null,
      
      // Iconos fijos (Inicio/Fin)
      currentPositionIcon: L.icon({
        iconUrl: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#E60023" width="30px" height="30px">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        `),
        iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -28]
      }),
      startPositionIcon: L.icon({
        iconUrl: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#0176D3" width="30px" height="30px">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        `),
        iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -28]
      }),
    };
  },
  watch: {
    historyPoints: {
      handler(newPoints) {
        if (this.map) {
          this.drawHistoryRoute(newPoints);
        }
      },
      deep: true,
      immediate: false, 
    },
    // Redibujar si las opciones de visualización cambian
    showNumberedMarkers() {
      if (this.map) {
        this.drawHistoryRoute(this.historyPoints);
      }
    },
    showFixedRoute() {
      if (this.map) {
        this.drawHistoryRoute(this.historyPoints);
      }
    }
  },
  mounted() {
    this.initMap();
    this.initMapEvents(); 

    this.$nextTick(() => {
      if (this.historyPoints.length > 0) {
        this.drawHistoryRoute(this.historyPoints);
      }
      this.invalidate();
    });
  },
  beforeDestroy() {
    const mapContainer = document.getElementById('leaflet-map-container');
    if (mapContainer) {
      mapContainer.removeEventListener('click', this.handleMapClick);
    }
    if (this.map) {
        this.map.remove();
    }
  },
  methods: {
    // --- LÓGICA DE COLOR PARA PUNTOS ---
    /**
     * @private
     * Convierte un valor (0 a 1) en un color de la escala de azul/verde/amarillo/rojo.
     * 0.0 (más antiguo) -> Púrpura/Azul Oscuro
     * 1.0 (más reciente) -> Amarillo/Rojo
     * @param {number} value - Valor normalizado entre 0 y 1.
     * @returns {string} Código de color HEX.
     */
    getTimeColor(value) {
        // Lógica de color ajustada para un gradiente de Púrpura/Azul (Viejo) a Amarillo/Rojo (Nuevo)
        
        let r, g, b;

        if (value < 0.25) {
            // Púrpura a Azul Oscuro
            value = value * 4;
            r = Math.floor(150 * (1 - value)); 
            g = 0;
            b = 150 + Math.floor(105 * value);
        } else if (value < 0.5) {
            // Azul a Cyan/Verde
            value = (value - 0.25) * 4;
            r = 0;
            g = Math.floor(200 * value);
            b = 255;
        } else if (value < 0.75) {
            // Verde a Amarillo
            value = (value - 0.5) * 4;
            r = Math.floor(255 * value);
            g = 255;
            b = 0;
        } else {
            // Amarillo a Rojo (Reciente)
            value = (value - 0.75) * 4;
            r = 255;
            g = 255 - Math.floor(255 * value);
            b = 0;
        }
        
        const toHex = c => Math.max(0, Math.min(255, c)).toString(16).padStart(2, '0');
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    },

    /**
     * Genera un ícono de marcador con color de tiempo o número SVG.
     */
    getHistoryPointIcon(color, number, showNumbered) {
        let svg;

        if (showNumbered) {
            // Ícono Numerado (siempre azul/cyan) - Mantenemos tamaño y opacidad por defecto aquí si no quieres que se superpongan
            svg = `
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#0194E3" width="28px" height="28px">
                  <circle cx="12" cy="12" r="10" fill="#0194E3"/>
                  <text x="12" y="15" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="white" text-anchor="middle" dominant-baseline="middle">${number}</text>
                </svg>
              `;
            // Ajustamos iconSize para el numerado
            return L.icon({
                iconUrl: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
                iconSize: [28, 28], // Aumentado ligeramente para el numerado
                iconAnchor: [14, 14], // Centro
                popupAnchor: [0, -12]
            });
        } else {
            // Ícono de Círculo con Color de Tiempo (TRANSPARENTE Y MÁS GRANDE)
            const circleRadius = 8; // Radio del círculo, aumentado de 6 a 8
            const circleSize = circleRadius * 2 + 4; // Tamaño del SVG para que el círculo quepa (ej. 20px)

            svg = `
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${circleSize} ${circleSize}" width="${circleSize}px" height="${circleSize}px">
                  <circle cx="${circleRadius + 2}" cy="${circleRadius + 2}" r="${circleRadius}" fill="${color}" fill-opacity="0.6"/> </svg>
              `;

            return L.icon({
                iconUrl: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
                iconSize: [circleSize, circleSize], // Tamaño total del icono
                iconAnchor: [circleSize / 2, circleSize / 2], // Anclaje en el centro
                popupAnchor: [0, -circleRadius] // Ajuste para el popup
            });
        }
    },

    // --- MÉTODOS EXISTENTES ---

    invalidate() {
      if (this.map) {
        this.map.invalidateSize(true); 
        console.log("Leaflet map size forcefully invalidated.");
      }
    },
    
    initMapEvents() {
      const mapContainer = document.getElementById('leaflet-map-container');
      if (mapContainer) {
          mapContainer.addEventListener('click', this.handleMapClick);
      }
    },

    handleMapClick(event) {
      const target = event.target;
      if (target && target.classList.contains('leaflet-popup-image-button')) {
          const base64Data = target.getAttribute('data-thumbnail');
          if (base64Data) {
            event.preventDefault(); 
            event.stopPropagation();
            this.openImagePopup(base64Data);
          }
      }
    },
    
    openImagePopup(base64Data) {
      this.currentThumbnailData = base64Data;
      
      this.$nextTick(() => {
        this.popupActivo = true;
      });
      
      console.log(`Abriendo popup con datos: ${base64Data ? 'Sí' : 'No'}`);
    },

    getThumbnailSrc(base64Data) {
      const placeholderUrl = 'https://placehold.co/400x300/cccccc/333333?text=Sin+Imagen+Disponible';

      if (base64Data && typeof base64Data === 'string' && base64Data.length > 100 && base64Data.trim() !== "-1") {
        return `data:image/jpeg;base64,${base64Data}`;
      } 
      return placeholderUrl;
    },

    initMap() {
      this.map = L.map('leaflet-map-container', {
        zoomControl: true,
        scrollWheelZoom: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(this.map);

      this.historyLayer = L.layerGroup().addTo(this.map);

      this.map.setView(this.defaultCoordinates, 13);
    },
    
    formatDate(value) {
      if (!value) return '';
      return moment(value).format('DD/MM/YYYY HH:mm:ss');
    },
    
    getPopupContent(points, isCurrent = false, isStart = false) { 
        // Lógica de Popup, se mantiene igual, ya que solo es HTML
        if (!points || points.length === 0) return '<div>No data available.</div>';
      
        const isMultiple = points.length > 1;

        let titleText = 'Punto de Historial';
        let titleStyle = '';
        
        if (isCurrent) {
            titleText = 'Posición Actual (Último Registro)';
            titleStyle = 'color: #E60023;'; 
        } else if (isStart) {
            titleText = 'Punto de Inicio (Primer Registro)';
            titleStyle = 'color: #0176D3;'; 
        } else if (isMultiple) {
            titleText = `Puntos Solapados (${points.length} registros)`;
            titleStyle = 'color: #1D4ED8;'; 
        } else {
            titleStyle = 'color: #1D4ED8;';
        }

        let htmlContent = `<div class="p-2" style="min-width: 250px; max-height: 300px; overflow-y: auto;">
                            <h4 class="font-bold text-lg mb-1" style="${titleStyle}">${titleText}</h4>
                            <hr class="mb-2"/>`;
        
        points.forEach((point, index) => {
            const isThisLast = (index === points.length - 1);
            
            let pointSequentialNumber = index + 1;

            const pointTitle = isMultiple 
                ? `<strong>• Registro Secuencial #${pointSequentialNumber} ${isThisLast ? '(Último en esta coord.)' : ''}</strong>` 
                : ''; 
              
            const timeStr = point.lastka_time ? this.formatDate(point.lastka_time) : 'N/A';
            const coordsStr = (point.lat != null && point.lon != null) 
                ? `${point.lat.toFixed(4)}, ${point.lon.toFixed(4)}` 
                : 'N/A';
            const gsmStr = point.gsm_signal != null ? `${point.gsm_signal.toFixed(2)}` : 'N/A';
            const velocity = point.velocity != null ? point.velocity.toFixed(1) : '0.0'; 
            
            const highlightClass = isThisLast && isMultiple ? 'border-2 border-red-300 bg-red-50' : 'bg-white';
            const highlightStyle = isThisLast && isMultiple ? 'border: 1px solid #E60023;' : 'border: 1px solid #ccc;';

            // LÓGICA DEL BOTÓN DE MINIATURA ACTUALIZADA
            let pointThumbnailLink = '';
            const buttonHtml = `
                <button 
                  class="leaflet-popup-image-button btn-vs-primary btn-vs-small"
                  data-thumbnail="${point.thumbnail}"
                  style="cursor: pointer;"
                  title="Haga clic para ver la imagen en el modal."
                >
                  Abrir Imagen Miniatura
                </button>
            `;

            if (point.thumbnail && typeof point.thumbnail === 'string' && point.thumbnail.length > 100 && point.thumbnail.trim() !== "-1") { 
              pointThumbnailLink = `<p class="ml-2 flex items-center justify-between">
                                      <span>Evidencia (Foto):</span> 
                                      ${buttonHtml}
                                    </p>`;
            } else {
              pointThumbnailLink = '<p class="ml-2 text-gray-500 text-sm">Evidencia (Foto): <strong>No Disponible</strong></p>';
            }

            htmlContent += `<div class="mb-3 p-2 rounded-lg ${highlightClass}" style="${highlightStyle}">
                              ${pointTitle}
                              <p class="ml-2">Fecha y Hora: <strong>${timeStr}</strong></p>
                              <p class="ml-2">Coordenadas: <strong>${coordsStr}</strong></p>
                              <p class="ml-2">Velocidad: <strong>${velocity} km/h</strong></p>
                              <p class="ml-2">Nivel de señal dBi: <strong>${gsmStr} dBi</strong></p>
                              ${pointThumbnailLink} 
                            </div>`;
        });
        
        htmlContent += `</div>`; 
        return htmlContent;
    },

    /**
     * Dibuja la polilínea (fija) y los marcadores (color por tiempo).
     */
    drawHistoryRoute(historyPoints) {
      if (!this.map) return;

      this.historyLayer.clearLayers();

      let validPoints = 0;
      const validHistoryPoints = historyPoints.filter(point => point.lat != null && point.lon != null);
      
      if (validHistoryPoints.length === 0) {
          this.map.setView(this.defaultCoordinates, 13);
          this.$emit('route-loaded', 0);
          return;
      }
      
      // 1. Pre-procesar: Agrupar por coordenada y determinar el rango de tiempo
      const pointsByCoord = new Map();
      let minTime = Infinity;
      let maxTime = -Infinity;
      
      validHistoryPoints.forEach(p => {
          const time = new Date(p.lastka_time).getTime();
          if (!isNaN(time)) {
              minTime = Math.min(minTime, time);
              maxTime = Math.max(maxTime, time);
          }

          const key = `${p.lat},${p.lon}`;
          if (!pointsByCoord.has(key)) {
              pointsByCoord.set(key, []);
          }
          pointsByCoord.get(key).push(p);
          validPoints++;
      });
      
      const timeRange = maxTime - minTime;
      const allCoordinates = validHistoryPoints.map(p => [p.lat, p.lon]);
      const defaultPolylineColor = '#0194E3'; // Azul Fijo

      // 2. Dibuja la polilínea fija (si está habilitado)
      if (this.showFixedRoute && validHistoryPoints.length > 1) {
          const polyline = L.polyline(allCoordinates, { color: defaultPolylineColor, weight: 4 }).addTo(this.historyLayer);
          
          // Agrega PolylineDecorator (flechas) si NO se muestran números
          if (!this.showNumberedMarkers && L.polylineDecorator) {
              L.polylineDecorator(polyline, {
                  patterns: [
                      {
                          offset: 30, 
                          repeat: 30, 
                          symbol: L.Symbol.arrowHead({
                              pathOptions: {
                                  fillOpacity: 1, 
                                  weight: 1, 
                                  color: defaultPolylineColor
                              },
                              pixelSize: 10, 
                              polygon: false
                          })
                      }
                  ]
              }).addTo(this.historyLayer);
          }
      }

      // 3. Itera para crear los marcadores (con color de tiempo o número)
      validHistoryPoints.forEach((point, index) => {
          const lat = point.lat;
          const lon = point.lon;
          const currentKey = `${lat},${lon}`;
          
          const isLastPoint = (index === validHistoryPoints.length - 1);
          const isFirstPoint = (index === 0);
          
          const nextKey = index + 1 < validHistoryPoints.length ? `${validHistoryPoints[index+1].lat},${validHistoryPoints[index+1].lon}` : null;
          // Solo colocamos un marcador si: es el inicio, es el final, o es el último punto en un conjunto de coordenadas solapadas.
          const isLastAtCoord = isLastPoint || (currentKey !== nextKey); 
          
          if (isLastPoint || isFirstPoint || (!isFirstPoint && !isLastPoint && isLastAtCoord)) {
              
              const groupedPointsAtThisLocation = pointsByCoord.get(currentKey);

              let markerIcon;
              let markerNumber = index + 1; 
              
              const pointTime = new Date(point.lastka_time).getTime();
              const normalizedTime = timeRange > 0 ? (pointTime - minTime) / timeRange : 0.5;
              const pointColor = this.getTimeColor(normalizedTime);

              if (isLastPoint) {
                  markerIcon = this.currentPositionIcon;
              } else if (isFirstPoint) {
                  markerIcon = this.startPositionIcon;
              } else {
                  // Puntos de historial: usan el ícono de color/número
                  markerIcon = this.getHistoryPointIcon(pointColor, markerNumber, this.showNumberedMarkers);
              }

              const popupContent = this.getPopupContent(groupedPointsAtThisLocation, isLastPoint, isFirstPoint);

              const marker = L.marker([lat, lon], {
                  icon: markerIcon,
                  title: isLastPoint ? 'Posición Actual (Fin)' : (isFirstPoint ? 'Posición de Inicio (Punto #1)' : (this.showNumberedMarkers ? `Punto #${markerNumber}` : 'Punto de Historial'))
              })
              .bindPopup(popupContent, {
                  autoClose: !isLastPoint, 
                  closeOnClick: !isLastPoint,
                  offset: isLastPoint || isFirstPoint ? L.point(0, -30) : L.point(0, -10),
              });
              
              this.historyLayer.addLayer(marker);

              // Abrir popup solo para la última posición por defecto
              if (isLastPoint) {
                  marker.openPopup();
              }
          }
      }); 

      // 4. Ajusta la vista del mapa
      if (allCoordinates.length > 1) {
          const polyline = L.polyline(allCoordinates);
          this.map.fitBounds(polyline.getBounds(), { 
              padding: [50, 50],
              maxZoom: 14 
          });
          this.$emit('route-loaded', validPoints);
      } else if (allCoordinates.length === 1) {
          this.map.setView(allCoordinates[0], 15);
          this.$emit('route-loaded', validPoints);
      } else {
          this.map.setView(this.defaultCoordinates, 13);
          this.$emit('route-loaded', 0);
      }
    }
  },
};
</script>

<style scoped>
/*
*** ESTILO CRÍTICO: Mínima Altura Garantizada ***
*/
#leaflet-map-container {
  height: 100%;
  width: 100%;
  min-height: 600px; /* ¡Altura mínima garantizada! */
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  background-color: #f8f8f8; 
}

/* --- Estilos para simular clases Tailwind en el popup (HTML puro) --- */
.font-bold {
    font-weight: bold;
}
.text-lg {
    font-size: 1.125rem;
}
.mb-1 {
    margin-bottom: 0.25rem;
}
.mb-2 {
    margin-bottom: 0.5rem;
}
.mb-3 {
    margin-bottom: 0.75rem;
}
.p-2 {
    padding: 0.5rem;
}
.rounded-lg {
    border-radius: 0.5rem;
}
.border-2 {
    border-width: 2px;
}
.border-red-300 {
    border-color: #fca5a5;
}
.bg-red-50 {
    background-color: #fef2f2;
}
.bg-white {
    background-color: #ffffff;
}
.text-gray-500 {
    color: #6b7280;
}
.text-sm {
    font-size: 0.875rem;
}

/* --- Estilos de Vuesax simulados para el botón del popup --- */
/* Estilos para simular el botón Vuesax (Primary, Filled) */
.btn-vs-primary {
  /* Fondo Primary de Vuesax */
  background-color: #0194E3; 
  color: #ffffff;
  /* Efecto Shadow/Filled */
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  /* Estilos base */
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
}

.btn-vs-primary:hover {
  background-color: #007acd; /* Tono más oscuro al pasar el ratón */
  box-shadow: 0 6px 8px -2px rgba(0, 0, 0, 0.15);
}

.btn-vs-small {
  /* Size Small */
  font-size: 0.75rem; /* 12px */
  padding: 4px 8px; /* py-1 px-2 */
  height: 24px;
  line-height: 16px; /* Ajuste para centrar texto verticalmente */
}

/* --- Clases Flex para alinear el texto y el botón --- */
.flex {
    display: flex;
}
.items-center {
    align-items: center;
}
.justify-between {
    justify-content: space-between;
}
.ml-2 {
    margin-left: 0.5rem;
}

/* Asegura que el botón personalizado sea clicable */
.leaflet-popup-image-button {
  display: inline-block;
  user-select: none;
  touch-action: manipulation;
  text-decoration: none;
}
/* --- Estilos para la Leyenda de Tiempo --- */
.time-legend-container {
    position: absolute;
    /* Ubicación: Esquina superior derecha */
    top: 20px;
    right: 20px; 
    z-index: 1000; /* Asegura que esté sobre el mapa */
    background: white;
    padding: 10px;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 93%;
}

.legend-title {
    font-size: 0.8rem;
    font-weight: bold;
    margin-bottom: 5px;
    color: #333;
    text-align: center;
}

.color-scale {
    display: flex;
    align-items: stretch;
    height: 100%; /* Altura de la barra de color */
}

.color-bar {
    width: 20px;
    height: 100%;
    border-radius: 4px;
    /* Este gradiente debe coincidir con la función getTimeColor en el JS:
       Más antiguo (bottom) -> Nuevo (top).
       El CSS lineal va de abajo (0%) a arriba (100%).
    */
    background: linear-gradient(
        to top, 
        /* Colores de tu escala (Púrpura/Azul a Amarillo/Verde) */
        #301934 0%, /* Más Antiguo (Púrpura/Oscuro - Corresponde a 0% o minTime) */
        #0000FF 25%,
        #00FF80 50%,
        #FFFF00 75%,
        #AAFF00 100% /* Más Reciente (Verde/Amarillo - Corresponde a 100% o maxTime) */
    );
    margin-right: 8px;
}

.time-labels {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    font-size: 0.75rem;
    font-weight: 500;
    color: #555;
}

.label.top {
    align-self: flex-start;
    margin-top: -5px; /* Ajuste visual para alinear con el borde */
    color: #008000; /* Color que indica "nuevo" */
}

.label.bottom {
    align-self: flex-start;
    margin-bottom: -5px; /* Ajuste visual */
    color: #551A8B; /* Color que indica "antiguo" */
}
</style>